/**
 * WARNING WARNING WARNING
 *
 * Usage of memoizeDeep can be dangerous: it can greatly improve
 * rendering of similar components on the same page (think multiple
 * primary buttons) by caching the themes. On the other hand, there
 * is a cost to cache all those properties, therefore it should not
 * be used lightly. A component that is rarely used (Footer,
 * Masthead, etc.) should not use this optimization.
 *
 * Key concepts to remember: memoizeDeep will cache arguments, even
 * if they are part of an object, so make sure that object is not
 * too deep. For example, passing the full theme to memoizeDeep would
 * be criminal (the theme.components contains ALL the components).
 *
 * Another key concept is that only native keys should be used:
 *
 * GOOD:
 * memoizeDeep(function({
 *  aaa, // -> boolean
 *  bbb, // -> string
 *  ccc, // -> number
 *  ddd, // -> simple object
 * }));
 *
 * BAD:
 * memoizeDeep(function({
 *  aaa, // -> JSX object
 *  bbb, // -> Deep nested objet
 *  ccc, // -> function
 * }));
 *
 */
import { memoizeDeep, safeMerge } from "../common/utilities";
const setTheme = memoizeDeep(function (_ref) {
  let {
    messagebar,
    theme,
    messageBarInModalType,
    showCloseButtonInFooter,
    showCloseButtonInHeader
  } = _ref;
  let cssRoot = {},
      messagebarRow = {},
      headerStyles = {},
      footerStyles = {},
      messagebarIconContainer = {},
      messagebarMessages = {},
      messagesContainer = {};

  if (messagebar) {
    cssRoot = safeMerge(cssRoot, messagebar.root);
    messagebarRow = messagebar.messagebarRow;
    headerStyles = messagebar.header;
    footerStyles = messagebar.footer;
    messagebarMessages = messagebar.messagebarMessages;
    messagesContainer = messagebar.messagesContainer;
    cssRoot = safeMerge(cssRoot, messagebar.theme[theme]);

    if (showCloseButtonInHeader) {
      cssRoot = safeMerge(cssRoot, messagebar.messagebarWithCloseStyles);
      messagebarRow = safeMerge(messagebarRow, messagebar.messagebarRowMargin);
    }
    /**
     * Account for MessageBar styles when it is within a traditional modal Panel.
     *
     * Note: When the MessageBar is within a "fullScreen" Panel, the styles are the
     * same as if the MessageBar was applied to a page without a Panel (default styles).
     */


    if (messageBarInModalType === "modal") {
      cssRoot = safeMerge(cssRoot, messagebar.modalOverrideStyles.root);
    }

    messagebar.messagebarIconContainer && (messagebarIconContainer = messagebar.messagebarIconContainer);

    if (showCloseButtonInFooter) {
      messagebarMessages = safeMerge(messagebarMessages, messagebar.footerCloseButton);
    }
  }

  return {
    cssRoot,
    messagebarRow,
    headerStyles,
    footerStyles,
    messagebarIconContainer,
    messagebarMessages,
    messagesContainer
  };
});
export default function (_ref2) {
  var _internalTheme$compon;

  let {
    internalTheme,
    theme,
    messageBarInModalType,
    showCloseButtonInFooter,
    showCloseButtonInHeader
  } = _ref2;
  let actualTheme = theme;
  const messagebar = internalTheme === null || internalTheme === void 0 ? void 0 : (_internalTheme$compon = internalTheme.components) === null || _internalTheme$compon === void 0 ? void 0 : _internalTheme$compon.messagebar;
  /**
   * In v4, some themes have been renamed... but to
   * remain backward compatible, we simply map the old
   * name to the new one.
   */

  switch (theme) {
    case "information":
      actualTheme = "informational";
      break;

    case "success":
      actualTheme = "confirmation";
      break;

    default:
      break;
  }

  return setTheme({
    messagebar,
    theme: actualTheme,
    messageBarInModalType,
    showCloseButtonInFooter,
    showCloseButtonInHeader
  });
}